import React from 'react';
import { FaAward, FaUsers, FaRocket, FaShieldAlt, FaHandshake, FaCode, FaCogs, FaBolt, FaMedal } from 'react-icons/fa';

const About = () => {
  const values = [
    {
      icon: <FaAward className="text-4xl" />,
      title: 'Technical Excellence',
      description: 'Engineering robust, scalable solutions using industry best practices and modern frameworks',
      gradient: 'from-yellow-500 to-orange-500',
    },
    {
      icon: <FaUsers className="text-4xl" />,
      title: 'Client Partnership',
      description: 'Collaborative relationships focused on achieving your strategic objectives and maximizing ROI',
      gradient: 'from-blue-500 to-primary',
    },
    {
      icon: <FaRocket className="text-4xl" />,
      title: 'Innovation',
      description: 'Leveraging emerging technologies to create competitive advantages and business value',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: <FaShieldAlt className="text-4xl" />,
      title: 'Security & Reliability',
      description: 'Enterprise-grade security and infrastructure ensuring business continuity and data protection',
      gradient: 'from-teal to-green-500',
    },
  ];

  const achievements = [
    { icon: <FaCode />, number: 'Full-Stack', label: 'Development' },
    { icon: <FaCogs />, number: 'End-to-End', label: 'Delivery' },
    { icon: <FaBolt />, number: 'Agile', label: 'Methodology' },
    { icon: <FaMedal />, number: '100%', label: 'Commitment' },
  ];

  const whyChooseUs = [
    {
      icon: <FaHandshake className="text-3xl" />,
      title: 'Proven Track Record',
      description: 'Demonstrated expertise delivering complex digital solutions across diverse industries, consistently exceeding client expectations and project objectives.',
    },
    {
      icon: <FaRocket className="text-3xl" />,
      title: 'Comprehensive Capabilities',
      description: 'Full-spectrum digital services from strategic planning and custom development to managed support, providing seamless end-to-end execution.',
    },
    {
      icon: <FaShieldAlt className="text-3xl" />,
      title: 'Strategic Partnership Approach',
      description: 'Dedicated account management, transparent communication, and proactive problem-solving ensuring your success is our success.',
    },
  ];

  return (
    <section id="about" className="relative py-20 md:py-28 bg-gradient-to-br from-gray-50 via-blue-50 to-white dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-40 w-96 h-96 bg-primary/5 dark:bg-teal/5 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-40 w-96 h-96 bg-teal/5 dark:bg-primary/5 rounded-full filter blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-accent/5 dark:bg-accent/10 rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20 animate-fade-in-up">
          <div className="inline-block">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              <span className="text-gray-900 dark:text-white">About </span>
              <span className="gradient-text">CitiTekPro</span>
            </h2>
            <div className="h-1 bg-gradient-to-r from-primary via-teal to-accent rounded-full"></div>
          </div>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mt-6">
            A digital agency committed to delivering innovative solutions that transform businesses and drive sustainable growth
          </p>
        </div>

        {/* Achievements Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6 mb-16 md:mb-20">
          {achievements.map((achievement, index) => (
            <div
              key={index}
              className="group relative bg-white dark:bg-gray-800 p-5 sm:p-6 md:p-7 lg:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 card-hover text-center animate-fade-in-up overflow-hidden"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Gradient Background on Hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-teal/10 dark:from-teal/20 dark:to-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="relative z-10">
                <div className="text-primary dark:text-teal mb-2 sm:mb-3 flex justify-center text-xl sm:text-2xl md:text-3xl group-hover:scale-110 transition-transform duration-300">
                  {achievement.icon}
                </div>
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text mb-1 sm:mb-2">
                  {achievement.number}
                </div>
                <div className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                  {achievement.label}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* About Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-16 md:mb-20">
          {/* Who We Are */}
          <div className="space-y-6 animate-slide-in-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 dark:bg-teal/10 rounded-full">
              <FaUsers className="text-primary dark:text-teal" />
              <span className="text-sm font-semibold text-primary dark:text-teal">Who We Are</span>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              Building Digital Excellence Since Day One
            </h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-lg">
              CitiTekPro is a full-service digital agency specializing in custom software development,
              enterprise web solutions, and strategic IT consulting. We partner with forward-thinking
              organizations to build scalable platforms, automate complex workflows, and implement
              technology strategies that create measurable competitive advantages.
            </p>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-lg">
              Our multidisciplinary team combines deep technical expertise with strategic business acumen,
              delivering solutions that align technology investments with organizational goals. From startups
              to established enterprises, we architect and implement digital ecosystems that drive efficiency,
              innovation, and sustainable growth.
            </p>
          </div>

          {/* Our Mission */}
          <div className="space-y-6 animate-slide-in-right">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal/10 dark:bg-primary/10 rounded-full">
              <FaRocket className="text-teal dark:text-primary" />
              <span className="text-sm font-semibold text-teal dark:text-primary">Our Mission</span>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              Driving Success Through Strategic Technology
            </h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-lg">
              Our mission is to empower businesses with technology solutions that deliver tangible ROI and
              sustainable competitive advantages. We believe exceptional digital products are built at the
              intersection of technical excellence, user-centered design, and strategic business alignment.
            </p>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-lg">
              Through transparent collaboration and agile methodologies, we transform complex business
              challenges into elegant technical solutions. Every engagement is guided by our commitment
              to quality, security, scalability, and long-term client success in an increasingly digital marketplace.
            </p>
          </div>
        </div>

        {/* Core Values */}
        <div className="mb-16 md:mb-20">
          <div className="text-center mb-12 animate-fade-in-up">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Core Values
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="group relative bg-white dark:bg-gray-800 p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 card-hover text-center animate-fade-in-up overflow-hidden"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Gradient Glow on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${value.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-2xl`}></div>

                <div className="relative z-10">
                  <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${value.gradient} text-white mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}>
                    {value.icon}
                  </div>
                  <h4 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    {value.title}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {value.description}
                  </p>
                </div>

                {/* Decorative Element */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br from-primary/10 to-teal/10 dark:from-teal/20 dark:to-primary/20 rounded-full filter blur-xl group-hover:scale-150 transition-transform duration-500"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="bg-gradient-to-r from-primary/10 via-teal/10 to-accent/10 dark:from-primary/20 dark:via-teal/20 dark:to-accent/20 p-8 md:p-12 rounded-3xl backdrop-blur-sm animate-fade-in-up">
          <div className="text-center mb-10">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose CitiTekPro?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
              Strategic advantages that set us apart in delivering transformative digital solutions
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                className="group bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${0.2 + index * 0.1}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 p-3 bg-gradient-to-br from-primary to-teal dark:from-teal dark:to-primary rounded-lg text-white group-hover:scale-110 transition-transform duration-300">
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {item.title}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
